import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, ArrowLeft, BarChart3, Loader2, Printer, RefreshCw,
  Search, SlidersHorizontal, XCircle,
} from "lucide-react";

import DatabankAnalysisReport, { printRef } from "../components/DatabankAnalysisReport";
import {
  useGetDatabankAnalysisQuery,
  useGetDatabankFiltersQuery,
} from "../api/databankApi";

const EMPTY = {
  state_id: "",
  category_id: "",
  sub_category_id: "",
  month: "",
  year: "",
  year_from: "",
  year_to: "",
  q: "",
};
type Filters = typeof EMPTY;

/**
 * Cost Databank — analysis.
 *
 * The browse screen answers "what does this item cost here?". This one answers
 * the questions a single row cannot: what the register holds, how far a price
 * moves between states, and — the part that decides work — which states it has
 * nothing on at all.
 *
 * It opens UNFILTERED, on the whole register, and takes the same filters the
 * browse list takes so a reader can carry a selection between the two screens.
 */
export default function DatabankAnalysisPage() {
  const printContentRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<Filters>({ ...EMPTY });
  // The search box is only committed on Enter / blur — a report is too
  // expensive to regenerate on every keystroke.
  const [searchDraft, setSearchDraft] = useState("");

  const { data: meta } = useGetDatabankFiltersQuery();
  const states = meta?.states ?? [];
  const categories = meta?.categories ?? [];
  const months = meta?.months ?? [];
  const years = meta?.years ?? [];

  const subCats = useMemo(
    () => categories.find((c) => String(c.id) === filters.category_id)?.sub_categories ?? [],
    [categories, filters.category_id],
  );

  const set = (k: keyof Filters) => (v: string) =>
    setFilters((f) => ({
      ...f,
      [k]: v,
      // A sub-category belongs to one category; changing the parent leaves any
      // previous child pointing at the wrong list, so it is cleared with it.
      ...(k === "category_id" ? { sub_category_id: "" } : {}),
    }));

  const { data, isLoading, isFetching, error, refetch } = useGetDatabankAnalysisQuery({
    state_id: filters.state_id || undefined,
    category_id: filters.category_id || undefined,
    sub_category_id: filters.sub_category_id || undefined,
    month: filters.month || undefined,
    year: filters.year || undefined,
    year_from: filters.year_from || undefined,
    year_to: filters.year_to || undefined,
    q: filters.q || undefined,
  });

  // Human labels, used for the page subtitle and printed into the report.
  const chips = useMemo(() => {
    const out: Array<{ label: string; value: string }> = [];
    const st = states.find((s) => String(s.id) === filters.state_id);
    if (st) out.push({ label: "State", value: st.name });
    const cat = categories.find((c) => String(c.id) === filters.category_id);
    if (cat) out.push({ label: "Category", value: cat.name });
    const sub = subCats.find((s: any) => String(s.id) === filters.sub_category_id);
    if (sub) out.push({ label: "Sub-category", value: sub.name });
    if (filters.month) out.push({ label: "Month", value: filters.month });
    if (filters.year) out.push({ label: "Year", value: String(filters.year) });
    if (filters.year_from) out.push({ label: "From", value: String(filters.year_from) });
    if (filters.year_to) out.push({ label: "To", value: String(filters.year_to) });
    if (filters.q) out.push({ label: "Search", value: filters.q });
    return out;
  }, [filters, states, categories, subCats]);

  const reportTitle = chips.length === 0
    ? "Whole Register — All States, All Periods"
    : chips.map((c) => c.value).join(" · ");

  const activeCount = chips.length;
  const handlePrint = () => printRef(printContentRef.current, reportTitle);

  // Full width on a phone, hugging its content from `sm` up: a row of five
  // selects that each shrink to their longest option is unreadable on a narrow
  // screen, and state and category names are long.
  const selectCls = (active: boolean) =>
    `w-full sm:w-auto min-w-0 text-[11px] font-semibold border rounded-lg px-2.5 py-1.5 outline-none transition-all cursor-pointer ${
      active
        ? "border-indigo-400 text-indigo-700 bg-indigo-50"
        : "border-gray-200 text-gray-600 bg-white hover:border-gray-400"
    }`;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3 no-print">
        <div>
          <Link
            to="/databank"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Cost Databank
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-black tracking-tight text-gray-900">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Databank Analysis
          </h1>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            {reportTitle}
            {activeCount > 0 && (
              <span className="text-indigo-600 font-bold">
                {" "}· {activeCount} filter{activeCount > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-gray-600 ${isFetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={handlePrint}
            disabled={isLoading || !!error}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50 transition-colors"
          >
            <Printer className="w-4 h-4" /> Print Report
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden no-print">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 px-4 sm:px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="text-[10px] font-black uppercase tracking-widest">Filters</span>
          </div>

          <select value={filters.state_id} onChange={(e) => set("state_id")(e.target.value)} className={selectCls(!!filters.state_id)}>
            <option value="">All States</option>
            {states.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>

          <select value={filters.category_id} onChange={(e) => set("category_id")(e.target.value)} className={selectCls(!!filters.category_id)}>
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>

          <select
            value={filters.sub_category_id}
            onChange={(e) => set("sub_category_id")(e.target.value)}
            disabled={!filters.category_id}
            className={`${selectCls(!!filters.sub_category_id)} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <option value="">All Sub-categories</option>
            {subCats.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>

          <select value={filters.month} onChange={(e) => set("month")(e.target.value)} className={selectCls(!!filters.month)}>
            <option value="">All Months</option>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>

          <select value={filters.year} onChange={(e) => set("year")(e.target.value)} className={selectCls(!!filters.year)}>
            <option value="">All Years</option>
            {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </select>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 px-4 sm:px-5 py-3 bg-gray-50/80">
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-300 shrink-0 sm:min-w-[44px]">Range</span>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">From</span>
            <select value={filters.year_from} onChange={(e) => set("year_from")(e.target.value)} className={selectCls(!!filters.year_from)}>
              <option value="">Earliest</option>
              {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </label>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">To</span>
            <select value={filters.year_to} onChange={(e) => set("year_to")(e.target.value)} className={selectCls(!!filters.year_to)}>
              <option value="">Latest</option>
              {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </label>

          <div className="relative w-full sm:w-auto">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onBlur={() => setFilters((f) => ({ ...f, q: searchDraft.trim() }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") setFilters((f) => ({ ...f, q: searchDraft.trim() }));
              }}
              placeholder="Search item or category…"
              className={`text-[11px] font-semibold border rounded-lg pl-7 pr-2.5 py-1.5 outline-none transition-all w-56 ${
                filters.q ? "border-indigo-400 text-indigo-700 bg-indigo-50" : "border-gray-200 text-gray-600 bg-white hover:border-gray-400"
              }`}
            />
          </div>

          {activeCount > 0 && (
            <button
              onClick={() => { setFilters({ ...EMPTY }); setSearchDraft(""); }}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-[10px] font-black uppercase tracking-wider hover:bg-red-100 transition-all"
            >
              <XCircle className="w-3.5 h-3.5" />
              Clear {activeCount} filter{activeCount > 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>

      {/* ── Report ── */}
      {/* The report keeps its table shape — it is a document people print, and
          reflowing it into cards would make the printed page disagree with the
          screen. But it does not scroll: the cells wrap instead, and the side
          padding is cut back on a phone so less of a narrow screen is margin. */}
      <div className="rounded-2xl border border-gray-200 bg-white px-3 sm:px-5 py-6 md:px-8">
        {isFetching && !isLoading && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100 no-print">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
            <span className="text-[11px] font-semibold text-indigo-600">Applying filters…</span>
          </div>
        )}

        {isLoading ? (
          <div className="py-32 flex flex-col items-center gap-4 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
            <p className="text-sm font-semibold">Generating databank analysis…</p>
            <p className="text-xs text-gray-300">Aggregating prices across states, categories and periods</p>
          </div>
        ) : error ? (
          <div className="py-24 text-center space-y-5">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-lg">Could not load analysis</p>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {(error as any)?.data?.message || "The server could not generate the report. Please try again."}
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className="px-6 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div ref={printContentRef}>
            <DatabankAnalysisReport data={data} title={reportTitle} filterChips={chips} />
          </div>
        )}
      </div>
    </div>
  );
}
